import * as yup from "yup";

import {
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  MenuItem,
  Paper,
  Slide,
  Stack,
  TextField,
  Tooltip,
  Typography
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { cyan, red } from "@mui/material/colors";
import { levelValue, levels, qaSkills } from "config";
import {
  useAddNewQaSkillsMutation,
  useDeleteQASkillsMutation,
  useEditCvQaSkillsMutation
} from "../../../lib/cvsSlice";

import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import LanguageIcon from "@mui/icons-material/Language";
import SaveIcon from "@mui/icons-material/Save";
import { styled } from "@mui/material/styles";
import { yupResolver } from "@hookform/resolvers/yup";

const cvSchema = yup.object().shape({
  skills: yup.string().required("QA skill is required"),
  level: yup.string().required("Level is required"),
});

// Styled components for MUI v5
const Dot = styled('div')(({ theme }) => ({
  height: theme.spacing(2),
  width: theme.spacing(2),
  borderRadius: "50%",
  marginRight: theme.spacing(0.5)
}));

const BlueDot = styled(Dot)({
  backgroundColor: "#2196f3"
});

const GreyDot = styled(Dot)({
  backgroundColor: "#9e9e9e"
});

const DotContainer = styled('div')({
  display: "flex",
  alignItems: "center"
});

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const renderDots = (level, maxCount) => {
  const dots = [];
  for (let i = 0; i < maxCount; i++) {
    if (i < level) {
      dots.push(<BlueDot key={i} />);
    } else {
      dots.push(<GreyDot key={i} />);
    }
  }
  return dots;
};

const QaSkills = ({ qaSkill, cvId }) => {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState();
  const [qaSkillState, setQaSkill] = useState(qaSkill);
  const [qaSkillsId, setQaSkillId] = useState();

  const handleOpen = (mode) => {
    setOpen(true);
    setMode(mode);
  };

  const handleClose = () => {
    reset();
    setOpen(false);
  };

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(cvSchema),
    defaultValues: {
      skills: "",
      level: "",
    },
  });

  const [addNewQaSkills] = useAddNewQaSkillsMutation();
  const submitResults = async (e) => {
    const newQaSkill = e;
    await addNewQaSkills({ cvId, qaSkill: [newQaSkill] }).unwrap();
    handleClose();
    qaSkill.push(newQaSkill); // add the new skill to the existing array
    toast.success("Programming Skill(s) added successfully");
  };



  const [deleteQASkills] = useDeleteQASkillsMutation();
  const handleRemove = async (item) => {
    await deleteQASkills({ cvId, qaSkillsId: item }).unwrap();
    const updatedQaSkills = qaSkillState.filter((qaskill) => qaskill.id !== item);
    setQaSkill(updatedQaSkills);

    qaSkill.splice(qaSkill.findIndex((qaskill) => qaskill._id === item), 1);
    setQaSkill([...qaSkill]);
    toast.info("Test Skill(s) removed successfully");
  };

  const [editCvQaSkills] = useEditCvQaSkillsMutation();
  const handleEdit = (qaSkillsId, mode) => {
    setOpen(true);
    setMode(mode);
    setQaSkillId(qaSkillsId);
  };

  useEffect(() => {
    const data = localStorage.getItem(`qaSkill_${cvId}`);
    if (data !== null) setQaSkill(JSON.parse(data));
  }, []);
  

  useEffect(() => {
    localStorage.setItem(`qaSkill_${cvId}`, JSON.stringify(qaSkill));
  }, [qaSkill, cvId]);



 const handleInputChange = (event, qaSkillsId, fieldName) => {
    const qaSkillElement = qaSkill.find((skill) => skill._id === qaSkillsId);
    qaSkillElement[fieldName] = event.target.value;

    const updatedQaSkill = qaSkill.map((skill) => {
      if (skill._id === qaSkillsId) {
        return { ...skill, [fieldName]: event.target.value };
      }
      return skill;
    });
    setQaSkill(updatedQaSkill);
  };


  const qaSkillElement = qaSkill.find((skill) => skill._id === qaSkillsId);

  console.log("qa skill", qaSkillElement)

  const renderSingleElement = () => {
    return (
      <Grid
        container
        spacing={2}
        justifyContent="center"
        alignItems="center"
        style={{ marginTop: "10px" }}
      >
        <Grid item xs={12}>
          <Controller
            name="skills"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Testing Skill(s)"
                select
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.skills}
                helperText={
                  errors?.skills ? errors.skills?.message : ""
                }
                value={qaSkillElement.skills}
                onChange={(e) => handleInputChange(e, qaSkillsId, "skills")}
              >
                {qaSkills.map((skills) => (
                  <MenuItem value={skills.name} key={skills.name}>
                    {skills.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Controller
            name="level"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                select
                label="Level"
                fullWidth
                SelectProps={{
                  MenuProps: {
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "center",
                    },
                  },
                }}
                error={!!errors.level}
                helperText={
                  errors?.level ? errors.level?.message : ""
                }
                value={qaSkillElement.level}
                onChange={(e) => handleInputChange(e, qaSkillsId, "level")}
              >
                {levels.map((level) => (
                  <MenuItem value={level.value} key={level.value}>
                    {level.name}
                  </MenuItem>
                ))}
              </TextField>
            )}
          />
        </Grid>


            
          </Grid>
        );
      };


      const submitEditResults = async () => {
    await editCvQaSkills({
      cvId,
      qaSkillsId,
      qaSkill: [qaSkillElement],
    }).unwrap();
    handleClose();
    toast.info("Programming Skills updated successfully");
  };
      

            
  return (
    <div id="qaSkills">
      <ToastContainer autoClose={1500} />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper style={{ marginTop: "20px", padding: "15px" }}>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="center"
              style={{ marginTop: "5px" }}
            >
              <Grid item xs={12}>
                <Grid
                  container
                  spacing={2}
                >
                  <Grid item xs={9}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      component="div"
                      sx={{ p: 2, pb: 0 }}
                    >
                      Testing Skills &nbsp;
                      <Tooltip
                        disableFocusListener
                        disableTouchListener
                        title="This section is mandatory"
                      >
                        <span style={{ color: "red" }}>*</span>
                      </Tooltip>
                    </Typography>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="add"
                      onClick={() => handleOpen("add")}
                    >
                      <AddIcon color="primary" />
                    </IconButton>
                  </Grid>
                  <Grid item xs={1}>
                    <IconButton
                      aria-label="edit"
                      onClick={() => handleOpen("edit")}
                    >
                      <EditIcon color="primary" />
                    </IconButton>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12}>
                {qaSkill.map((skills) => (
                  <List key={skills._id} style={{ paddingLeft: "10px" }}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar style={{ backgroundColor: cyan[700] }}>
                          <LanguageIcon />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        style={{ paddingLeft: "10px" }}
                        primary={
                          <Grid container alignItems="flex-start" xs={12} key={skills._id} display="flex" mb={1}>
                            <Grid item xs={2}>
                              <Typography variant="subtitle2" components="div">{skills.skills}</Typography>
                            </Grid>
                            <Grid item display="column" xs={3}>
                              <DotContainer>
                                {renderDots(Number(skills.level), 5)}
                              </DotContainer>
                              <Typography variant="subtitle2" components="div">{levelValue[Number(skills.level) - 1].name}</Typography>
                            </Grid>
                          </Grid>
                        }
                      />
                    </ListItem>
                  </List>
                ))}
              </Grid>
            </Grid>
          </Paper>
        </Grid>
      </Grid>
      <Dialog
        open={open}
        TransitionComponent={Transition}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
      >
        {mode === "add" && (
          <form autoComplete="off" onSubmit={handleSubmit(submitResults)}>
            <DialogTitle>Add a New Testing Skill </DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "10px" }}
              >
                <Grid item xs={12}>
                  <Controller
                    name="skills"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="Testing Skill(s)"
                        select
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        error={!!errors.skills}
                        helperText={
                          errors?.skills ? errors.skills?.message : ""
                        }
                      >
                        {qaSkills.map((skills) => (
                          <MenuItem value={skills.name} key={skills.name}>
                            {skills.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="level"
                    control={control}
                    rules={{ required: true }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        label="Level"
                        fullWidth
                        SelectProps={{
                          MenuProps: {
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "center",
                            },
                          },
                        }}
                        value={field.value}
                        error={!!errors.level}
                        helperText={
                          errors?.level ? errors.level?.message : ""
                        }
                      >
                        {levels.map((level) => (
                          <MenuItem value={level.value} key={level.value}>
                            {level.name}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
        {mode === "edit" && (
          <form>
            <DialogTitle>Edit/Remove QA Skill(s)</DialogTitle>
            <DialogContent dividers={true}>
              <Grid
                container
                spacing={2}
                justifyContent="center"
                alignItems="center"
                style={{ marginTop: "5px" }}
              >
                <Grid item xs={12}>
                  {qaSkill?.map((skills) => (
                    <List key={skills._id}>
                      <ListItem
                        style={{ marginRight: "-2rem" }}
                        secondaryAction={
                          <Stack alignItems="left" direction="row">
                            <IconButton
                              aria-label="edit"
                              onClick={() => handleEdit(skills._id, "editnew")}
                            >
                              <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                              aria-label="delete"
                              onClick={() => handleRemove(skills._id)}
                            >
                              <DeleteIcon style={{ color: red[400] }} />
                            </IconButton>
                          </Stack>
                        }
                      >
                        <ListItemText
                          style={{ paddingRight: "20px", margin: "0px" }}
                          primary={
                            <Typography variant="subtitle2" component="div">
                              {skills.skills}
                            </Typography>
                          }
                          secondary={
                            <Typography variant="caption" display="block">
                              {levelValue[Number(skills.level) - 1].name}
                            </Typography>
                          }
                        />
                      </ListItem>
                    </List>
                  ))}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                      disabled
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}

        {mode === "editnew" && (
          <form
            autoComplete="off"
            onSubmit={() => {
              submitEditResults();
            }}
          >
            <DialogTitle>Edit QA Skill(s)</DialogTitle>
            <DialogContent dividers={true}>
              {renderSingleElement()}
            </DialogContent>
            <DialogActions>
              <Grid
                container
                spacing={2}
                style={{ margin: "10px" }}
                justifyContent="flex-end"
              >
                <Grid item xs={12}>
                  <Stack alignItems="right" direction="row" spacing={3}>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<SaveIcon />}
                      type="submit"
                    >
                      Save
                    </Button>
                    <Button
                      sx={{ borderRadius: 10 }}
                      variant="contained"
                      startIcon={<CancelIcon />}
                      onClick={handleClose}
                    >
                      Cancel
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </DialogActions>
          </form>
        )}
      </Dialog>
    </div>
  );
};

export default QaSkills;